import { calculateNetReceive } from "../utils/pricingCalculations";

const CompactRateCard = ({
  type,
  rates,
  calculateBalance,
  color,
  textColor,
}) => {
  const isRequired = type === "Adult" || type === "Child";
  const balanceFn = calculateBalance || calculateNetReceive;

  return (
    <div className={`${color} rounded-lg p-3 border border-gray-200 min-w-0`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
          {type}
          {isRequired && <span className="text-red-500 ml-0.5">*</span>}
        </span>
        <span className={`text-sm font-semibold whitespace-nowrap tabular-nums ${textColor}`}>
          ₹{balanceFn(rates.basePrice, rates.adminFee)}
        </span>
      </div>
      <div className="space-y-1.5 text-xs">
        <div className="flex items-center justify-between gap-3">
          <span className="text-gray-500 whitespace-nowrap shrink-0">
            Base{isRequired && <span className="text-red-500 ml-0.5">*</span>}
          </span>
          <span className="font-medium text-gray-800 tabular-nums whitespace-nowrap">
            ₹{rates.basePrice}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-gray-500 whitespace-nowrap shrink-0">
            Admin{isRequired && <span className="text-red-500 ml-0.5">*</span>}
          </span>
          <span className="font-medium text-gray-800 tabular-nums whitespace-nowrap">
            {rates.adminFee}%
          </span>
        </div>
      </div>
    </div>
  );
};

const RateCard = ({
  rate,
  colors,
  calculateBalance,
  formatDate,
  weekdays,
  isStopSale = false,
  onEdit,
  onDelete,
}) => {
  if (isStopSale) {
    const days = rate.stop_sale_applicable_days;
    const activeDays = [];
    if (days.sun) activeDays.push("Sun");
    if (days.mon) activeDays.push("Mon");
    if (days.tue) activeDays.push("Tue");
    if (days.wed) activeDays.push("Wed");
    if (days.thu) activeDays.push("Thu");
    if (days.fri) activeDays.push("Fri");
    if (days.sat) activeDays.push("Sat");

    return (
      <div className="group p-3 hover:bg-gray-50 transition-colors">
        <div className="flex items-center justify-between mb-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <span
              className={`${colors["stop"]} px-2 py-1 rounded-md text-xs capitalize font-semibold`}
            >
              Stop Sales
            </span>
            <div className="flex items-center text-xs font-semibold">
              <span className="bg-gray-100 px-2 py-1 rounded">
                <i className="fi fi-rr-calendar mr-2 relative"></i>
                {formatDate(rate.start_date)}
              </span>
              <i className="fi fi-rr-arrow-right mx-2"></i>
              <span className="bg-gray-100 px-2 py-1 rounded">
                <i className="fi fi-rr-calendar mr-2 relative"></i>
                {formatDate(rate.end_date)}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {activeDays.length === weekdays.length ? (
                <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs">
                  All Days
                </span>
              ) : (
                activeDays.map((day) => (
                  <span
                    key={day}
                    className="px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs"
                  >
                    {day}
                  </span>
                ))
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
            <button
              type="button"
              onClick={() => onEdit?.(rate)}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-600"
              title="Edit stop sale"
            >
              <i className="fi fi-rr-edit text-xs"></i>
            </button>
            <button
              type="button"
              onClick={() => onDelete?.(rate.id)}
              className="p-1.5 rounded-md hover:bg-red-50 text-gray-500 hover:text-red-600"
              title="Delete stop sale"
            >
              <i className="fi fi-rr-trash text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group p-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between mb-0">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span
            className={`${
              colors[rate.rateType]
            } px-2 py-1 rounded-md text-xs capitalize font-semibold`}
          >
            {rate.rateType === "stop" ? "Stop Sales" : rate.rateType}
          </span>
          <div className="flex items-center text-xs font-semibold">
            <span className="bg-gray-100 px-2 py-1 rounded">
              <i className="fi fi-rr-calendar mr-2 relative"></i>
              {formatDate(rate.startDate)}
            </span>
            <i className="fi fi-rr-arrow-right mx-2"></i>
            <span className="bg-gray-100 px-2 py-1 rounded">
              <i className="fi fi-rr-calendar mr-2 relative"></i>
              {formatDate(rate.endDate)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
          <button
            type="button"
            onClick={() => onEdit?.(rate)}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-600"
            title="Edit rate"
          >
            <i className="fi fi-rr-edit text-xs"></i>
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(rate.id)}
            className="p-1.5 rounded-md hover:bg-red-50 text-gray-500 hover:text-red-600"
            title="Delete rate"
          >
            <i className="fi fi-rr-trash text-xs"></i>
          </button>
        </div>
      </div>

      {rate.rateType !== "stop" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 min-w-0">
          {["Adult", "Child", "Infant"].map((type) => (
            <CompactRateCard
              key={type}
              type={type}
              rates={rate.rates[type.toLowerCase()]}
              calculateBalance={calculateBalance}
              color="bg-gray-50 hover:bg-gray-100"
              textColor={
                type === "Adult"
                  ? "text-blue-600"
                  : type === "Child"
                  ? "text-green-600"
                  : "text-purple-600"
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RateCard;
